import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, Save, Search, Upload, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { API, inr, imgUrl, backendUrl } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const PAYMENT_TERMS = ["100% Advance", "50% Advance, 50% on Delivery", "Net 15 Days", "Net 30 Days", "Cash on Delivery"];
const DEFAULT_TC = "1. Prices are valid for the quotation validity period only.\n2. Returns are accepted as per our Sale Return policy — please report issues within 7 days of delivery.\n3. Delivery timelines are indicative and subject to stock availability.\n4. Payment must be made as per the agreed payment terms.\n5. All disputes are subject to local jurisdiction only.";
const emptyItem = { product_id: "", name: "", description: "", image_url: "", qty: 1, rate: 0, tax_pct: 18 };

function DocPreview({ type, firm, customer, items, totals, discountPct, docDate, number, bank, terms, transport, validUntil }) {
  const co = firm || {};
  const coName = co.business_name || co.name || "Your Firm";
  const bn = bank || {};
  const rows = items.filter((i) => (i.name || "").trim());
  const tncLines = (terms || "").split("\n").filter((l) => l.trim());
  const num = number || (type === "quotation" ? "QT-____" : "INV-____");
  if (type === "quotation") {
    const gstPct = rows[0]?.tax_pct ?? 18;
    const wm = coName ? coName.charAt(0).toUpperCase() + coName.slice(1).toLowerCase() : coName;
    return (
      <div className="relative overflow-hidden bg-white text-[9px] leading-snug text-slate-900" data-testid="doc-preview">
        <div className="bg-gradient-to-br from-[#1B2A6B] to-[#0F1A4D] px-6 pb-8 pt-6 text-white">
          <div className="flex items-center gap-2">
            {co.logo_url ? <img src={imgUrl(co.logo_url)} alt="logo" className="h-7 w-7 rounded-md bg-white object-contain p-0.5" /> : null}
            <span className="text-[15px] font-bold">{wm}</span>
          </div>
          <div className="mt-6 text-[20px] font-extrabold">PERFORMA INVOICE</div>
          <div className="mt-1 text-[9px] text-[#9FB0EE]">Quotation{validUntil ? ` · valid until ${validUntil}` : ""}</div>
        </div>
        <div className="p-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-slate-300 p-2">
            <div className="text-[8px] font-bold text-[#1B2A6B]">INVOICE FROM</div>
            <div className="mt-1 font-bold uppercase">{coName}</div>
            <div className="whitespace-pre-line">{co.address || ""}</div>
            <div>CONTACT:- {co.phone || ""}</div>
            {co.gstin ? <div>GSTIN:- {co.gstin}</div> : null}
          </div>
          <div className="rounded-md border border-slate-300 p-2">
            <div className="text-[8px] font-bold text-[#1B2A6B]">INVOICE TO</div>
            <div className="mt-1 font-bold uppercase">{customer.name || "Customer Name"}</div>
            {customer.company ? <div>{customer.company}</div> : null}
            <div>BILLING ADRESS</div>
            <div className="whitespace-pre-line">{customer.address || ""}</div>
            <div>CONTACT:- {customer.phone || ""}</div>
            <div>GSTIN:- {customer.gstin || "N/A"}</div>
          </div>
        </div>
        <table className="mt-3 w-full border-collapse text-center">
          <thead>
            <tr className="bg-[#1B2A6B] text-[8px] font-bold text-white">
              <th className="px-1 py-1">No.</th><th>Image</th><th>Description</th><th>Pcs</th><th>Price</th><th>GST</th><th>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((it, i) => (
              <tr key={i} className="border-b border-slate-300">
                <td className="border-x border-slate-300 px-1 py-1">{i + 1}</td>
                <td className="border-x border-slate-300">{it.image_url ? <img src={imgUrl(it.image_url)} alt="" className="mx-auto h-7 w-7 object-cover" /> : ""}</td>
                <td className="border-x border-slate-300">{it.name}{it.description ? <span className="block text-[8px] text-slate-400">{it.description}</span> : null}</td>
                <td className="border-x border-slate-300">{Number(it.qty) || 0}</td>
                <td className="border-x border-slate-300">{inr(Number(it.rate) || 0)}</td>
                <td className="border-x border-slate-300">{Number(it.tax_pct ?? 18)}%</td>
                <td className="border-x border-slate-300">{inr((Number(it.qty) || 0) * (Number(it.rate) || 0))}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="py-3 text-slate-400">Add items to see the preview</td></tr>}
          </tbody>
        </table>
        <div className="mt-14 border-t border-slate-800 pt-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="font-bold">Payment Method</div>
              {bn.bank_name ? <div>BANK&nbsp;&nbsp;&nbsp;: {bn.bank_name}</div> : null}
              {bn.bank_holder ? <div>NAME&nbsp;&nbsp;: {bn.bank_holder}</div> : null}
              {bn.bank_account ? <div>A/C No&nbsp;: {bn.bank_account}</div> : null}
              {bn.bank_ifsc ? <div>IFSC&nbsp;&nbsp;&nbsp;&nbsp;: {bn.bank_ifsc}</div> : null}
              {bn.bank_branch ? <div>BRANCH&nbsp;: {bn.bank_branch}</div> : null}
              {bn.qr_url ? <img src={imgUrl(bn.qr_url)} alt="QR" className="mt-1 h-14 w-14 object-contain" /> : null}
            </div>
            <div>
              <div className="flex justify-between"><span>Sub-total</span><span>{inr(totals.subtotal)}/-</span></div>
              {totals.discount > 0 && <div className="flex justify-between"><span>Discount ({discountPct}%)</span><span>- {inr(totals.discount)}</span></div>}
              <div className="flex justify-between"><span>Transport</span><span>{inr(transport || 0)}/-</span></div>
              <div className="flex justify-between"><span>GST ({gstPct}%)</span><span>{inr(totals.tax)}/-</span></div>
              <div className="mt-1 flex justify-between bg-slate-800 px-2 py-1.5 font-bold text-white"><span>Total</span><span>{inr(totals.total)}/-</span></div>
            </div>
          </div>
        </div>
        {tncLines.length > 0 && (
          <div className="mt-2">
            <div className="font-bold">Terms and Conditions</div>
            {tncLines.map((l, i) => <div key={i}>{i + 1}. {l.replace(/^\d+[.)]\s*/, "")}</div>)}
          </div>
        )}
        <div className="mt-3 border-t-2 border-[#1B2A6B] pt-2 text-right">
          <div className="font-bold text-[#1B2A6B]">For, {coName.toUpperCase()}</div>
          <div className="mt-6 font-bold">{coName}</div>
          <div>Administrator</div>
        </div>
        </div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-md border border-slate-400 bg-white text-[9px] leading-snug text-slate-800" data-testid="doc-preview">
      <div className="py-2 text-center text-[14px] font-bold text-slate-800">Tax Invoice</div>
      <div className="mx-2 flex gap-2 border border-slate-400 p-2">
        {co.logo_url ? <img src={imgUrl(co.logo_url)} alt="logo" className="h-10 w-10 object-contain" /> : null}
        <div>
          <div className="text-[12px] font-bold uppercase text-slate-900">{coName}</div>
          <div className="text-slate-500">{co.address || ""}</div>
          <div>Phone: <b>{co.phone || "-"}</b>&nbsp;&nbsp;Email: <b>{co.email || "-"}</b></div>
          <div>GSTIN: <b>{co.gstin || "-"}</b></div>
        </div>
      </div>
      <div className="mx-2 grid grid-cols-2 border-x border-b border-slate-400">
        <div className="border-r border-slate-400 p-2">
          <div className="font-bold">Bill To:</div>
          <div className="font-semibold uppercase">{customer.name || "Customer Name"}</div>
          <div className="whitespace-pre-line text-slate-500">{customer.address || ""}</div>
          <div>Contact No: <b>{customer.phone || "-"}</b>&nbsp;&nbsp;GSTIN: <b>{customer.gstin || "-"}</b></div>
        </div>
        <div className="p-2">
          <div className="font-bold">Invoice Details:</div>
          <div>Invoice No.: <b>{num}</b></div>
          <div>Date: <b>{docDate}</b></div>
        </div>
      </div>
      <table className="mx-2 w-[calc(100%-16px)] border border-slate-400">
        <thead>
          <tr className="border-b border-slate-400 text-left">
            <th className="px-1 py-1">#</th><th>Item name</th><th>HSN/SAC</th><th className="text-right">Qty</th><th className="text-right">Price(₹)</th><th className="text-right">GST(₹)</th><th className="px-1 text-right">Amount(₹)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => {
            const line = (Number(it.qty) || 0) * (Number(it.rate) || 0);
            const gst = (line - (line * (Number(discountPct) || 0)) / 100) * ((Number(it.tax_pct) || 0) / 100);
            return (
              <tr key={i} className="border-b border-slate-200">
                <td className="px-1 py-1">{i + 1}</td>
                <td className="font-medium">{it.name}</td>
                <td>{it.hsn || "-"}</td>
                <td className="text-right">{Number(it.qty) || 0}</td>
                <td className="text-right">{inr(Number(it.rate) || 0)}</td>
                <td className="text-right">{inr(gst)} ({Number(it.tax_pct) || 0}%)</td>
                <td className="px-1 text-right">{inr(line + gst)}</td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={7} className="py-3 text-center text-slate-400">Add items to see the preview</td></tr>}
        </tbody>
      </table>
      <div className="mx-2 grid grid-cols-2 border-x border-b border-slate-400">
        <div className="border-r border-slate-400 p-2">
          <div className="font-bold">Bank Details:</div>
          {bn.bank_name ? <div>Name : <b>{bn.bank_name}</b></div> : null}
          {bn.bank_account ? <div>Account No. : <b>{bn.bank_account}</b></div> : null}
          {bn.bank_ifsc ? <div>IFSC code : <b>{bn.bank_ifsc}</b></div> : null}
          {bn.bank_holder ? <div>Account holder's name : <b>{bn.bank_holder}</b></div> : null}
          {bn.qr_url ? <img src={imgUrl(bn.qr_url)} alt="QR" className="mt-1 h-12 w-12 object-contain" /> : null}
        </div>
        <div className="p-2">
          <div className="flex justify-between"><span>Sub Total :</span><span>{inr(totals.subtotal)}</span></div>
          {(transport || 0) > 0 && <div className="flex justify-between"><span>Transport :</span><span>{inr(transport)}</span></div>}
          <div className="flex justify-between"><span>GST Total :</span><span>{inr(totals.tax)}</span></div>
          <div className="flex justify-between font-bold"><span>Total :</span><span>{inr(totals.total)}</span></div>
          <div className="mt-1 text-right text-slate-500">For {coName.toUpperCase()}:</div>
        </div>
      </div>
      {tncLines.length > 0 && (
        <div className="mx-2 mb-2 border-x border-b border-slate-400 p-2">
          <div className="font-bold">Terms &amp; Conditions:</div>
          {tncLines.map((l, i) => <div key={i} className="text-slate-500">{l}</div>)}
        </div>
      )}
    </div>
  );
}

export default function DocumentBuilder() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [type, setType] = useState(params.get("type") || "quotation");
  const [docStatus, setDocStatus] = useState("draft");
  const [channel, setChannel] = useState("Direct");
  const [poNo, setPoNo] = useState("");
  const [poDate, setPoDate] = useState("");
  const [ewayBill, setEwayBill] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [shipTo, setShipTo] = useState({ name: "", address: "", phone: "" });
  const [sameAsBill, setSameAsBill] = useState(true);
  const [dirty, setDirty] = useState(false);
  const loadedRef = useRef(false);
  useEffect(() => { const t = setTimeout(() => { loadedRef.current = true; }, 1500); return () => clearTimeout(t); }, [id, type]);
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  const leadIdParam = params.get("lead_id") || "";
  const fromParam = params.get("from") || "";
  const [docDate, setDocDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [amountReceived, setAmountReceived] = useState("");
  const [payAccount, setPayAccount] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attUploading, setAttUploading] = useState(false);
  const attRef = useRef(null);
  const [firmBanks, setFirmBanks] = useState([]);
  const [firm2, setFirm2] = useState(null);
  const [bankSel, setBankSel] = useState("");
  const [bankInit, setBankInit] = useState("");
  const [newItemFor, setNewItemFor] = useState(null);
  const [newItemForm, setNewItemForm] = useState({ name: "", sell_price: "", hsn: "" });
  const [newItemSaving, setNewItemSaving] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [staticPages, setStaticPages] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [quoteSearch, setQuoteSearch] = useState("");
  const [quoteListOpen, setQuoteListOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [leadId, setLeadId] = useState(leadIdParam);
  const [customer, setCustomer] = useState({ name: "", company: "", phone: "", email: "", address: "", gstin: "" });
  const [customerId, setCustomerId] = useState("none");
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [discountPct, setDiscountPct] = useState(0);
  const [transport, setTransport] = useState("");
  const [paymentTerms, setPaymentTerms] = useState(PAYMENT_TERMS[0]);
  const [terms, setTerms] = useState(DEFAULT_TC);
  const [validUntil, setValidUntil] = useState("");
  const [dueDate, setDueDate] = useState("");
  useEffect(() => { if (loadedRef.current) setDirty(true); }, [customer, items, terms, discountPct, paymentTerms, validUntil, dueDate, number, docDate]);
  const [upperPage, setUpperPage] = useState("none");
  const [lowerPage, setLowerPage] = useState("none");
  const [saving, setSaving] = useState(false);

  const itemsFromLead = (leadItems) =>
    leadItems.map((i) => ({ product_id: i.product_id || "", name: i.name || "", description: i.description || "", image_url: i.image_url || "", qty: i.qty || 1, rate: i.rate || 0, tax_pct: i.tax_pct ?? 18 }));

  useEffect(() => {
    API.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    API.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    API.get("/static-pages").then((r) => setStaticPages(r.data)).catch(() => {});
    API.get("/settings/config").then(async (c) => {
      const { data: fs } = await API.get("/firms");
      const f2 = fs.find((f) => f.id === c.data.firm2_id);
      setFirm2(f2 || null);
      const banks = f2?.banks?.length
        ? f2.banks
        : f2?.bank_name
          ? [{ bank_name: f2.bank_name, bank_holder: f2.bank_holder, bank_account: f2.bank_account, bank_ifsc: f2.bank_ifsc, bank_branch: f2.bank_branch, qr_url: f2.qr_url || "" }]
          : [];
      setFirmBanks(banks);
    }).catch(() => {});
    if (type === "invoice" && !id) {
      API.get("/documents", { params: { type: "quotation" } }).then((r) => setQuotations(r.data)).catch(() => {});
    }
    if (type === "invoice") {
      API.get("/accounting/accounts").then((r) => setAccounts(r.data)).catch(() => setAccounts([]));
    }
    if (id) {
      API.get(`/documents/${id}`).then((r) => {
        const d = r.data;
        setNumber(d.number);
        setType(d.type);
        setDocStatus(d.status || "draft");
        setLeadId(d.lead_id || "");
        setCustomer(d.customer);
        setCustomerId(d.customer_id || "none");
        setItems(d.items.length ? d.items.map((i) => ({ ...i, tax_pct: i.tax_pct ?? 18 })) : [{ ...emptyItem }]);
        setDiscountPct(d.discount_pct);
        setTransport(d.transport || "");
        setPaymentTerms(d.payment_terms || PAYMENT_TERMS[0]);
        setTerms(d.terms || DEFAULT_TC);
        setValidUntil(d.valid_until || "");
        setChannel(d.channel || "Direct");
        setDueDate(d.due_date || "");
        setDocDate(d.doc_date || String(d.created_at).slice(0, 10));
        setAttachmentUrl(d.attachment_url || "");
        setPoNo(d.po_no || "");
        setPoDate(d.po_date || "");
        setEwayBill(d.eway_bill || "");
        setPlaceOfSupply(d.place_of_supply || "");
        setShipTo(d.ship_to || { name: "", address: "", phone: "" });
        setSameAsBill(!d.ship_to);
        setBankSel("");
        setBankInit(d.bank_snapshot?.bank_name || "");
        setUpperPage((d.upper_pages || [])[0] || "none");
        setLowerPage((d.lower_pages || [])[0] || "none");
      }).catch(() => {});
    } else if (fromParam) {
      API.get(`/documents/${fromParam}`).then((r) => {
        const q = r.data;
        setLeadId(q.lead_id || "");
        setCustomer(q.customer);
        setCustomerId(q.customer_id || "none");
        setItems(q.items?.length ? q.items.map((i) => ({ ...i, tax_pct: i.tax_pct ?? 18 })) : [{ ...emptyItem }]);
        setDiscountPct(q.discount_pct || 0);
        setPaymentTerms(q.payment_terms || PAYMENT_TERMS[0]);
        setTerms(q.terms || DEFAULT_TC);
        setDueDate(q.due_date || "");
        setUpperPage((q.upper_pages || [])[0] || "none");
        setLowerPage((q.lower_pages || [])[0] || "none");
        toast.success(`Details loaded from ${q.number} — complete the invoice info and save`);
      }).catch(() => toast.error("Could not load the linked performa invoice"));
    } else if (leadIdParam) {
      API.get(`/leads/${leadIdParam}`).then(async (r) => {
        const l = r.data;
        let cust = { name: l.name || "", company: l.company || "", phone: l.phone || "", email: l.email || "", address: l.address || "", gstin: "" };
        if (l.customer_id) {
          setCustomerId(l.customer_id);
          try {
            const cr = await API.get(`/customers/${l.customer_id}`);
            cust = {
              name: cr.data.name || cust.name,
              company: cr.data.company || "",
              phone: cr.data.phone || "",
              email: cr.data.email || "",
              address: cr.data.address || l.address || "",
              gstin: cr.data.gstin || "",
            };
          } catch {}
        }
        setCustomer(cust);
        if (l.items?.length) {
          setItems(itemsFromLead(l.items));
          toast.success("Customer & products loaded from lead");
        } else {
          toast.success("Customer loaded from lead");
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, type]);

  useEffect(() => {
    if (bankInit && firmBanks.length) {
      const idx = firmBanks.findIndex((b) => b.bank_name === bankInit);
      if (idx >= 0) setBankSel(idx);
      setBankInit("");
    }
  }, [firmBanks, bankInit]);

  useEffect(() => {
    if (!id && firm2?.tnc) setTerms(firm2.tnc);
  }, [firm2]);

  const pickCustomer = async (cid) => {
    setCustomerId(cid);
    const c = customers.find((x) => x.id === cid);
    if (c) setCustomer({ name: c.name, company: c.company || "", phone: c.phone || "", email: c.email || "", address: c.address || "", gstin: c.gstin || "" });
    const itemsEmpty = items.length === 1 && !items[0].name.trim();
    if (cid !== "none" && itemsEmpty) {
      try {
        const { data: lead } = await API.get(`/customers/${cid}/lead`);
        if (lead.items?.length) {
          setItems(itemsFromLead(lead.items));
          toast.success("Products loaded from lead requirements");
        }
      } catch {}
    }
  };

  const pickQuotation = (q) => {
    setCustomer(q.customer || { name: "", company: "", phone: "", email: "", address: "", gstin: "" });
    setCustomerId(q.customer_id || "none");
    setItems(q.items?.length ? q.items.map((i) => ({ ...i, tax_pct: i.tax_pct ?? 18 })) : [{ ...emptyItem }]);
    setDiscountPct(q.discount_pct || 0);
    setPaymentTerms(q.payment_terms || PAYMENT_TERMS[0]);
    setTerms(q.terms || DEFAULT_TC);
    setUpperPage((q.upper_pages || [])[0] || "none");
    setLowerPage((q.lower_pages || [])[0] || "none");
    setQuoteListOpen(false);
    setQuoteSearch("");
    toast.success(`Loaded details from ${q.number}`);
  };

  const filteredQuotes = quotations.filter((q) => {
    const s = quoteSearch.toLowerCase();
    return !s || q.number.toLowerCase().includes(s) || (q.customer?.name || "").toLowerCase().includes(s);
  });

  const setItem = (i, patch) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setItem(i, { product_id: pid, name: p.name, description: p.description || "", image_url: p.image_url || "", rate: p.sell_price || 0, hsn: p.hsn || "" });
    else setItem(i, { product_id: "", name: "", description: "", image_url: "", rate: 0 });
  };

  const createNewItem = async () => {
    if (!newItemForm.name.trim()) return toast.error("Item name is required");
    setNewItemSaving(true);
    try {
      const { data } = await API.post("/products", { name: newItemForm.name.trim(), sell_price: Number(newItemForm.sell_price) || 0, hsn: newItemForm.hsn, type: "product" });
      setProducts((p) => [...p, data]);
      if (newItemFor !== null) setItem(newItemFor, { product_id: data.id, name: data.name, description: data.description || "", image_url: data.image_url || "", rate: data.sell_price || 0, hsn: newItemForm.hsn });
      toast.success("New item saved to Inventory and selected");
      setNewItemFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create item");
    } finally {
      setNewItemSaving(false);
    }
  };

  const totals = useMemo(() => {
    const disc = Number(discountPct) || 0;
    const tr = Number(transport) || 0;
    const subtotal = items.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
    const discount = (subtotal * disc) / 100;
    const tax = items.reduce((a, i) => {
      const line = (Number(i.qty) || 0) * (Number(i.rate) || 0);
      return a + (line - (line * disc) / 100) * ((Number(i.tax_pct) || 0) / 100);
    }, 0);
    return { subtotal, discount, tax, transport: tr, total: subtotal - discount + tax + tr };
  }, [items, discountPct, transport]);

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/upload", fd);
      setAttachmentUrl(data.url || "");
      toast.success("Attachment uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setAttUploading(false);
      if (attRef.current) attRef.current.value = "";
    }
  };

  const save = async () => {
    const cleanItems = items.filter((i) => i.name.trim());
    if (!customer.name.trim()) return toast.error("Customer name is required");
    if (type === "invoice" && !terms.trim()) return toast.error("Terms & Conditions are required for invoices");
    if (cleanItems.length === 0) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const payload = {
        type,
        number: number.trim(),
        customer_id: customerId === "none" ? "" : customerId,
        lead_id: leadId || "",
        customer,
        items: cleanItems.map((i) => ({ ...i, qty: Number(i.qty) || 0, rate: Number(i.rate) || 0, tax_pct: Number(i.tax_pct) ?? 18 })),
        discount_pct: Number(discountPct) || 0,
        transport: Number(transport) || 0,
        tax_pct: 18,
        payment_terms: paymentTerms,
        terms,
        status: id ? docStatus : "sent",
        valid_until: validUntil,
        channel,
        due_date: dueDate,
        po_no: poNo,
        po_date: poDate,
        eway_bill: ewayBill,
        place_of_supply: placeOfSupply,
        ship_to: type === "invoice" && !sameAsBill ? shipTo : null,
        doc_date: docDate,
        bank_snapshot: bankSel !== "" ? (firmBanks[bankSel] || {}) : {},
        source_quote_id: fromParam,
        attachment_url: attachmentUrl,
        upper_pages: upperPage === "none" ? [] : [upperPage],
        lower_pages: lowerPage === "none" ? [] : [lowerPage],
      };
      if (id) {
        await API.patch(`/documents/${id}`, payload);
        toast.success("Updated — lead requirements synced");
      } else {
        if (type === "invoice" && Number(amountReceived) > 0 && !payAccount) {
          setSaving(false);
          return toast.error("Select the account for the received amount");
        }
        const { data: created } = await API.post("/documents", payload);
        if (type === "invoice" && Number(amountReceived) > 0 && payAccount) {
          try {
            await API.post("/accounting/payments", { date: docDate, type: "receipt", party: customer.name, amount: Number(amountReceived), account_id: payAccount, ref: created.number, invoice_id: created.id, note: "Received at billing" });
            toast.success(`Receipt recorded against ${created.number}`);
          } catch (e) {
            toast.error(e.response?.data?.detail || "Receipt could not be saved");
          }
        }
        toast.success(`${type === "quotation" ? "Performa Invoice" : "Sale Invoice"} created`);
      }
      navigate(type === "invoice" ? "/accounts/sales/invoices" : "/documents");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const pageOptions = (value, onChange, testid) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue placeholder="None" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">None</SelectItem>
        {staticPages.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} {p.pdf_name ? "(PDF)" : ""} {p.category ? `· ${p.category}` : ""}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-4" data-testid="document-builder">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => { if (dirty && !window.confirm("Current changes will be discarded. Do you want to continue?")) return; navigate(type === "invoice" ? "/accounts/sales/invoices" : "/documents"); }} data-testid="builder-back-button" aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {id ? `Edit ${number}` : `New ${type === "quotation" ? "Performa Invoice" : "Sale Invoice"}`}
          </h1>
          <p className="text-sm text-slate-500">Form on the left, live A4 preview on the right — both scroll independently</p>
        </div>
      </div>

      {type === "invoice" && !id && (
        <Card className="border-indigo-200 bg-indigo-50/40">
          <CardContent className="pt-5">
            <Label className="text-sm font-semibold text-slate-800">Import from Quotation (optional)</Label>
            <div className="relative mt-1.5">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search quotation number or customer..."
                value={quoteSearch}
                onChange={(e) => { setQuoteSearch(e.target.value); setQuoteListOpen(true); }}
                onFocus={() => setQuoteListOpen(true)}
                className="bg-white pl-8"
                data-testid="quote-search-input"
              />
              {quoteListOpen && filteredQuotes.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid="quote-search-results">
                  {filteredQuotes.slice(0, 20).map((q) => (
                    <button
                      key={q.id}
                      onClick={() => pickQuotation(q)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors duration-100 hover:bg-indigo-50"
                      data-testid={`quote-pick-${q.id}`}
                    >
                      <span><span className="font-semibold text-indigo-700">{q.number}</span> · {q.customer?.name || "-"}</span>
                      <span className="text-xs text-slate-500">{inr(q.total)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-1 text-[11px] text-slate-500">Selecting a quotation fills customer, items, taxes and terms automatically.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[58%_minmax(0,42%)]">
        {/* LEFT — form panel, scrolls independently on desktop */}
        <div className="space-y-4 lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto lg:pr-2" data-testid="builder-form-panel">
          {/* Row 1: Customer (60%) + Document (40%) */}
          <div className="grid gap-4 md:grid-cols-5">
            <Card className="border-slate-200 md:col-span-3" data-testid="customer-card">
              <CardHeader><CardTitle className="text-base">Customer</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Pick from CRM</Label>
                  <Select value={customerId} onValueChange={pickCustomer}>
                    <SelectTrigger className="mt-1.5" data-testid="builder-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Manual entry</SelectItem>
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] text-slate-400">Picking a customer also pulls their lead's product requirements.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Name *</Label><Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="mt-1.5" data-testid="builder-customer-name" /></div>
                  <div className="col-span-2"><Label>Company</Label><Input value={customer.company} onChange={(e) => setCustomer({ ...customer, company: e.target.value })} className="mt-1.5" data-testid="builder-customer-company" /></div>
                  <div><Label>Phone (WhatsApp)</Label><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="mt-1.5" data-testid="builder-customer-phone" /></div>
                  <div><Label>Email</Label><Input value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="mt-1.5" data-testid="builder-customer-email" /></div>
                  <div className="col-span-2"><Label>Address</Label><Input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} className="mt-1.5" data-testid="builder-customer-address" /></div>
                  <div className="col-span-2"><Label>GSTIN</Label><Input value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value })} className="mt-1.5" data-testid="builder-customer-gstin" /></div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 md:col-span-2">
              <CardHeader><CardTitle className="text-base">Document</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{type === "quotation" ? "Performa Invoice" : "Sale Invoice"} Number</Label>
                  <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Auto (e.g. QT-0001)" className="mt-1.5" data-testid="doc-number-input" />
                  <p className="mt-1 text-[11px] text-slate-400">Leave blank for auto-numbering. Next auto number follows the latest number.</p>
                </div>
                <div>
                  <Label>Upper Static Page (before quote)</Label>
                  {pageOptions(upperPage, setUpperPage, "upper-page-select")}
                </div>
                <div>
                  <Label>Lower Static Page (after quote)</Label>
                  {pageOptions(lowerPage, setLowerPage, "lower-page-select")}
                </div>
                {type === "invoice" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>PO No.</Label><Input value={poNo} onChange={(e) => setPoNo(e.target.value)} className="mt-1.5" data-testid="po-no-input" /></div>
                      <div><Label>PO Date</Label><Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} className="mt-1.5" data-testid="po-date-input" /></div>
                    </div>
                    <div><Label>E-way Bill No.</Label><Input value={ewayBill} onChange={(e) => setEwayBill(e.target.value)} className="mt-1.5" data-testid="eway-bill-input" /></div>
                    <div><Label>Place of Supply</Label><Input value={placeOfSupply} onChange={(e) => setPlaceOfSupply(e.target.value)} placeholder="e.g. Maharashtra (27)" className="mt-1.5" data-testid="place-of-supply-input" /></div>
                    <div>
                      <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                        <input type="checkbox" checked={!sameAsBill} onChange={(e) => setSameAsBill(!e.target.checked)} className="accent-indigo-600" data-testid="ship-to-toggle" /> Different Ship To address
                      </label>
                      {!sameAsBill && (
                        <div className="mt-2 space-y-2 rounded-md border border-slate-200 p-3" data-testid="ship-to-form">
                          <Input value={shipTo.name} onChange={(e) => setShipTo({ ...shipTo, name: e.target.value })} placeholder="Recipient name" data-testid="ship-to-name" />
                          <Input value={shipTo.address} onChange={(e) => setShipTo({ ...shipTo, address: e.target.value })} placeholder="Delivery address" data-testid="ship-to-address" />
                          <Input value={shipTo.phone} onChange={(e) => setShipTo({ ...shipTo, phone: e.target.value })} placeholder="Phone" data-testid="ship-to-phone" />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Line Items (full width) */}
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Line Items</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setItems((p) => [...p, { ...emptyItem }])} data-testid="add-item-button">
                <Plus className="mr-1 h-4 w-4" /> Add Item
              </Button>
            </CardHeader>
            <CardContent className="space-y-4" data-testid="items-list">
              {items.map((it, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-4" data-testid={`item-row-${i}`}>
                  <div className="grid grid-cols-12 items-start gap-3">
                    <div className="col-span-12 md:col-span-1">
                      {it.image_url ? (
                        <img src={imgUrl(it.image_url)} alt="" className="h-12 w-12 rounded-md border border-slate-200 object-cover" />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-slate-100 text-xs text-slate-400">No img</div>
                      )}
                    </div>
                    <div className="col-span-12 md:col-span-3">
                      <Label className="text-xs">Product</Label>
                      <Select value={it.product_id || "custom"} onValueChange={(v) => { if (v === "__new") { setNewItemFor(i); setNewItemForm({ name: "", sell_price: "", hsn: "" }); } else pickProduct(i, v === "custom" ? "" : v); }}>
                        <SelectTrigger className="mt-1" data-testid={`item-product-select-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Custom item</SelectItem>
                          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          <SelectItem value="__new" data-testid={`item-product-new-${i}`}>+ Add as new item (saves to Inventory)</SelectItem>
                        </SelectContent>
                      </Select>
                      {!it.product_id && <Input placeholder="Item name *" value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} className="mt-2" data-testid={`item-name-${i}`} />}
                    </div>
                    <div className={`col-span-6 ${type === "invoice" ? "md:col-span-2" : "md:col-span-3"}`}>
                      <Label className="text-xs">Description</Label>
                      <Input value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} className="mt-1" data-testid={`item-description-${i}`} />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" min="0" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className="mt-1" data-testid={`item-qty-${i}`} />
                    </div>
                    <div className="col-span-3 md:col-span-2">
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input type="number" min="0" value={it.rate} onChange={(e) => setItem(i, { rate: e.target.value })} className="mt-1" data-testid={`item-rate-${i}`} />
                    </div>
                    {type === "invoice" && (
                      <div className="col-span-6 md:col-span-1">
                        <Label className="text-xs">HSN/SAC</Label>
                        <Input value={it.hsn || ""} onChange={(e) => setItem(i, { hsn: e.target.value })} className="mt-1" data-testid={`item-hsn-${i}`} />
                      </div>
                    )}
                    <div className="col-span-6 md:col-span-1">
                      <Label className="text-xs">GST %</Label>
                      <Input type="number" min="0" max="28" step="0.1" value={it.tax_pct} onChange={(e) => setItem(i, { tax_pct: e.target.value })} className="mt-1" data-testid={`item-gst-${i}`} />
                    </div>
                    <div className="col-span-6 flex items-end justify-between md:col-span-1 md:flex-col md:items-end">
                      <div className="mt-1 text-sm font-semibold text-slate-900">{inr((Number(it.qty) || 0) * (Number(it.rate) || 0))}</div>
                      <Button variant="ghost" size="icon" onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} disabled={items.length === 1} data-testid={`item-delete-${i}`} aria-label="Remove item">
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Row 3: T&C + Summary + Payment & Validity (equal width) */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Terms &amp; Conditions{type === "invoice" ? " *" : ""}</CardTitle></CardHeader>
              <CardContent>
                <Textarea rows={8} value={terms} onChange={(e) => setTerms(e.target.value)} data-testid="builder-terms-input" />
                <p className="mt-1 text-[11px] text-slate-400">Default comes from Settings → Company 2 — edits here apply to this document only.</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Summary</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium" data-testid="summary-subtotal">{inr(totals.subtotal)}</span></div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Discount %</span>
                  <Input type="number" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} className="h-8 w-20 text-right" data-testid="discount-input" />
                </div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Discount</span><span>- {inr(totals.discount)}</span></div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Transport (₹)</span>
                  <Input type="number" min="0" value={transport} onChange={(e) => setTransport(e.target.value)} className="h-8 w-20 text-right" data-testid="transport-input" />
                </div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">GST Total (item-wise)</span><span data-testid="summary-gst">{inr(totals.tax)}</span></div>
                <div className="flex justify-between border-t border-slate-200 pt-3">
                  <span className="font-semibold text-slate-900">Grand Total</span>
                  <span className="font-heading text-lg font-bold text-indigo-600" data-testid="summary-total">{inr(totals.total)}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Payment &amp; Validity</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Document Date</Label>
                  <Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} className="mt-1.5" data-testid="doc-date-input" />
                </div>
                {firmBanks.length > 0 && (
                  <div>
                    <Label>Payment Receive Bank (printed on PDF)</Label>
                    <Select value={bankSel === "" ? "default" : String(bankSel)} onValueChange={(v) => setBankSel(v === "default" ? "" : Number(v))}>
                      <SelectTrigger className="mt-1.5" data-testid="payment-bank-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Firm default (first bank)</SelectItem>
                        {firmBanks.map((b, bi) => <SelectItem key={bi} value={String(bi)} data-testid={`payment-bank-option-${bi}`}>{b.bank_name}{b.bank_account ? ` · ${b.bank_account}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Payment Terms</Label>
                  <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                    <SelectTrigger className="mt-1.5" data-testid="payment-terms-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {type === "quotation" ? (
                  <div><Label>Valid Until</Label><Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1.5" data-testid="valid-until-input" /></div>
                ) : (
                  <>
                    <div><Label>Due Date</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1.5" data-testid="due-date-input" /></div>
                    <div>
                      <Label>Sales Channel</Label>
                      <Select value={channel} onValueChange={setChannel}>
                        <SelectTrigger className="mt-1.5" data-testid="invoice-channel-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Direct", "Amazon", "Flipkart", "IndiaMart", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {!id && (
                      <>
                        <div>
                          <Label>Amount Received Now (optional)</Label>
                          <Input type="number" min="0" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} className="mt-1.5" placeholder="0" data-testid="amount-received-input" />
                        </div>
                        {Number(amountReceived) > 0 && (
                          <div>
                            <Label>Received In Account</Label>
                            <Select value={payAccount} onValueChange={setPayAccount}>
                              <SelectTrigger className="mt-1.5" data-testid="pay-account-select"><SelectValue placeholder="Select cash/bank account" /></SelectTrigger>
                              <SelectContent>
                                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                <div>
                  <Label>Attachment (optional)</Label>
                  <input ref={attRef} type="file" className="hidden" onChange={uploadAttachment} data-testid="attachment-input" />
                  {attachmentUrl ? (
                    <div className="mt-1.5 flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid="attachment-chip">
                      <a href={`${backendUrl}${attachmentUrl}`} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 text-sm text-indigo-700 hover:underline">
                        <Paperclip className="h-4 w-4 shrink-0" />
                        <span className="truncate">View attachment</span>
                      </a>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAttachmentUrl("")} data-testid="attachment-remove" aria-label="Remove attachment">
                        <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" className="mt-1.5 w-full border-dashed" onClick={() => attRef.current?.click()} disabled={attUploading} data-testid="attachment-upload-button">
                      <Upload className="mr-2 h-4 w-4" /> {attUploading ? "Uploading..." : "Upload Attachment"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={saving} data-testid="save-document-button">
            <Save className="mr-2 h-4 w-4" /> {saving ? "Saving..." : `Save ${type === "quotation" ? "Performa Invoice" : "Sale Invoice"}`}
          </Button>
        </div>

        {/* RIGHT — live preview panel, scrolls independently on desktop */}
        <div className="lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto" data-testid="builder-preview-panel">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Live Preview — A4</CardTitle></CardHeader>
            <CardContent>
              <div className="mx-auto aspect-[210/297] w-full overflow-y-auto rounded-md bg-white shadow-md ring-1 ring-slate-200">
                <DocPreview type={type} firm={firm2} customer={customer} items={items} totals={totals} discountPct={discountPct} docDate={docDate} number={number} bank={bankSel !== "" ? firmBanks[bankSel] : firmBanks[0]} terms={terms} transport={Number(transport) || 0} validUntil={validUntil} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={newItemFor !== null} onOpenChange={(o) => !o && setNewItemFor(null)}>
        <DialogContent data-testid="new-item-dialog">
          <DialogHeader><DialogTitle>Add New Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Creates the product in Inventory and selects it on this line.</p>
            <div><Label>Item Name *</Label><Input value={newItemForm.name} onChange={(e) => setNewItemForm({ ...newItemForm, name: e.target.value })} className="mt-1.5" data-testid="new-item-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Sell Price (₹)</Label><Input type="number" min="0" value={newItemForm.sell_price} onChange={(e) => setNewItemForm({ ...newItemForm, sell_price: e.target.value })} className="mt-1.5" data-testid="new-item-price" /></div>
              <div><Label>HSN/SAC</Label><Input value={newItemForm.hsn} onChange={(e) => setNewItemForm({ ...newItemForm, hsn: e.target.value })} className="mt-1.5" data-testid="new-item-hsn" /></div>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={createNewItem} disabled={newItemSaving} data-testid="new-item-save">{newItemSaving ? "Saving..." : "Create & Select"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
